import { useState } from 'react'
import styled from 'styled-components'
import { Input } from 'antd'
import { useTranslation } from 'react-i18next'
import { Modal } from 'components/reusables/modal'
import { ModalButtons } from 'components/reusables/modalButtons'
import RequiredFieldLabel from 'components/reusables/requiredFieldLabel'
import { TRANSLATION_PREFIX } from '../../constants'

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 16px;
`

const ErrorText = styled.p`
  margin: 0;
  font-size: 12px;
  line-height: 16px;
  color: #cf1322;
`

interface IProps {
  initialName: string
  initialDescription: string
  saving: boolean
  nameError: string | null
  onConfirm: (values: { name: string; description: string }) => void
  onClose: () => void
}

/** Name and description of the new survey of the bank made from a course survey. */
export const SaveToBankModal = ({
  initialName,
  initialDescription,
  saving,
  nameError,
  onConfirm,
  onClose,
}: IProps) => {
  const { t } = useTranslation()
  const [name, setName] = useState(initialName)
  const [description, setDescription] = useState(initialDescription)
  const [touched, setTouched] = useState(false)
  const prefix = `${TRANSLATION_PREFIX}.block.saveModal`
  const missingName = touched && !name.trim()
  const error = missingName ? t(`${prefix}.nameRequired`) : nameError

  const confirm = () => {
    setTouched(true)
    if (!name.trim()) return
    onConfirm({ name: name.trim(), description })
  }

  return (
    <Modal
      title={t(`${prefix}.title`)}
      close={onClose}
      maxwidth={560}
      maxheight={480}
      roboto
      actions={
        <ModalButtons
          secondaryText={t(`${prefix}.cancel`)}
          secondaryAction={onClose}
          primaryText={t(`${prefix}.confirm`)}
          primaryAction={confirm}
          processing={saving}
        />
      }
    >
      <div data-testid="survey-block-save-modal">
        <Field>
          <RequiredFieldLabel required>
            <label htmlFor="survey-bank-save-name">{t(`${prefix}.name`)}</label>
          </RequiredFieldLabel>
          <Input
            id="survey-bank-save-name"
            data-testid="survey-block-save-name"
            value={name}
            aria-required
            aria-invalid={!!error}
            onChange={(event) => setName(event.target.value)}
            autoFocus
          />
          {error && (
            <ErrorText role="alert" data-testid="survey-block-save-name-error">
              {error}
            </ErrorText>
          )}
        </Field>
        <Field>
          <label htmlFor="survey-bank-save-description">
            {t(`${prefix}.description`)}
          </label>
          <Input.TextArea
            id="survey-bank-save-description"
            data-testid="survey-block-save-description"
            rows={3}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>
      </div>
    </Modal>
  )
}
